// --------------------------------------------------------------
// Created On: 2025-03-12
// Author: Hannah Vaughan
//
// Last Modified: 2025-11-06
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment } from "react";
import SortedTableHeader from "../../../components/SortedTableHeader/SortedTableHeader";
import formatDashTimeString from "../../../utilities/formatDashTimeString";
import formatDateLocal from "../../../utilities/time/formatDateLocal";
import styles from "./OrderHistoryTable.module.scss";

// Table containing information on all sales orders that fit within the passed-in filters.
export default function OrderHistoryTable(props: Props): Component {
  // Formats the order date by removing the time from the string then converting it into a slash format.
  function formatOrderDate(orderDate: string | null): string {
    if (orderDate !== null && orderDate.trim() !== "") {
      let dateString = formatDateLocal(orderDate);
      const newDate = new Date(dateString);
      dateString = newDate.toISOString().split("T")[0];
      return formatDashTimeString(dateString);
    } else {
      return "";
    }
  }

  // Retrieves the order's status in string format based on the presence of timestamps
  // for the order being received, picked, and/or shipped.
  function getOrderStatusString(order: SalesOrder): string {
    if (order.shippedUtc !== null) {
      return "Shipped";
    } else if (order.shippedUtc === null && order.pickedUtc !== null) {
      return "Picked";
    } else {
      return "Received";
    }
  }

  return (
    <Fragment>
      {!props.loading && props.salesOrders.length === 0 && (
        <div className={`${styles.info} py-5 px-4`}>
          <h5 className="mb-4">
            <span>No sales orders exist</span>
          </h5>
        </div>
      )}
      {!props.loading && props.salesOrders.length > 0 && (
        <table className={`${styles.orderTable} table`}>
          <thead className={styles.header}>
            <tr>
              <SortedTableHeader
                name="Order Date"
                index={1}
                selectedIndex={props.sortColumnIndex}
                selectedAscending={props.sortAscending}
                onClick={(index) => props.onUpdateSort(index)}
                className="col-2"
              />
              <SortedTableHeader
                name="PO #"
                index={2}
                selectedIndex={props.sortColumnIndex}
                selectedAscending={props.sortAscending}
                onClick={(index) => props.onUpdateSort(index)}
                className="col-2"
              />
              <SortedTableHeader
                name="Sales Order #"
                index={3}
                selectedIndex={props.sortColumnIndex}
                selectedAscending={props.sortAscending}
                onClick={(index) => props.onUpdateSort(index)}
                className="col-2"
              />
              <SortedTableHeader
                name="Grand Total"
                index={4}
                selectedIndex={props.sortColumnIndex}
                selectedAscending={props.sortAscending}
                onClick={(index) => props.onUpdateSort(index)}
                className="col-2"
              />
              <th className={`${styles.unsortedColumn} col-2`}>Status</th>
              <th className={`${styles.unsortedColumn} col-2`}>View Details</th>
            </tr>
          </thead>
          <tbody>
            {props.salesOrders.map((order) => (
              <tr key={order.salesOrderId}>
                <td className="align-middle">{formatOrderDate(order.receivedUtc)}</td>
                <td className="align-middle">{order.poNumber}</td>
                <td className="align-middle">{order.epicorSalesOrderNumber}</td>
                <td className="align-middle">
                  ${order.grandTotal.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                </td>
                <td className="align-middle">{getOrderStatusString(order)}</td>
                <td className="align-middle">
                  <button
                    className="btn btn-primary btn-sm text-center px-3"
                    onClick={() => props.onUpdateSelection(order.salesOrderId)}
                  >
                    <i className={`fa fa-fw fa-external-link mx-2 ${styles.viewDetailsButton}`} />
                    View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Fragment>
  );
}

interface Props {
  loading: boolean;
  salesOrders: SalesOrder[];
  sortColumnIndex: number;
  sortAscending: boolean;
  onUpdateSort: (newIndex: number) => void;
  onUpdateSelection: (salesOrderId: number) => void;
}

interface SalesOrder {
  salesOrderId: number;
  companyId: number;
  userId: number;
  epicorSalesOrderNumber: string;
  poNumber: string;
  grandTotal: number;
  receivedUtc: string | null;
  pickedUtc: string | null;
  shippedUtc: string | null;
}
